/**
 * bdiHighlight.js — iter820 (Feb 2026)
 *
 * Tiny React context that powers the bidirectional
 *   Condition-chip  ↔  BDI-card  hover-highlight.
 *
 * Because both consumers (ConditionChipsRow, BdiGenoLab) live on the
 * SAME page (BreastHealthIntelligencePage), a lightweight React context
 * is the right tool — no window events, no persistence, resets on unmount.
 */
import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

const Ctx = createContext({
  hoveredCondition: null,
  hoveredDiseaseId: null,
  cardsByCondition: {},         // { condition_id: Set<disease_id> }
  conditionsByCard: {},         // { disease_id: Set<condition_id> }
  setHoveredCondition: () => {},
  setHoveredDiseaseId: () => {},
  registerCards: () => {},
});

export function BdiHighlightProvider({ children }) {
  const [hoveredCondition, setHoveredCondition] = useState(null);
  const [hoveredDiseaseId, setHoveredDiseaseId] = useState(null);
  const [cardsByCondition, setCardsByCondition] = useState({});
  const [conditionsByCard, setConditionsByCard] = useState({});

  // Called by BdiGenoLab once its cards[] are computed. Rebuilds the two
  // reverse-index maps from the condition_influences on each card.
  const registerCards = useCallback((cards) => {
    const byC = {};
    const byD = {};
    for (const card of cards || []) {
      const did = card.disease_id;
      const infs = card.condition_influences || [];
      byD[did] = new Set();
      for (const inf of infs) {
        const cid = inf.condition_id;
        if (!cid) continue;
        byD[did].add(cid);
        if (!byC[cid]) byC[cid] = new Set();
        byC[cid].add(did);
      }
    }
    setCardsByCondition(byC);
    setConditionsByCard(byD);
  }, []);

  // iter821 — MUST memoize value or every provider render creates a new
  // context reference, which causes any consumer using the whole ctx as
  // a useEffect dep to loop infinitely (see iteration_820.json RCA).
  const value = useMemo(
    () => ({
      hoveredCondition, hoveredDiseaseId,
      cardsByCondition, conditionsByCard,
      setHoveredCondition, setHoveredDiseaseId, registerCards,
    }),
    [hoveredCondition, hoveredDiseaseId, cardsByCondition, conditionsByCard, registerCards],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useBdiHighlight() {
  return useContext(Ctx);
}
